/**
 * Wishlist Back-in-Stock (BIS) Modal
 *
 * A single, shared "Notify me when back in stock" modal used across the
 * wishlist experiences (the drawer in wishlist.js and the shared wishlist
 * page in wishlist-page.js). It is lazily created and appended to <body> the
 * first time it's opened, then reused for the lifetime of the page.
 *
 * It posts directly to Klaviyo's client back-in-stock endpoint, mirroring the
 * product page's BIS form.
 *
 * Public API (window.WishlistBIS):
 *   open(variantId, productTitle)  — show the modal for a given variant
 *   close()                        — hide the modal
 */
(function () {
  'use strict';

  const COMPANY_ID = window.YJ?.klaviyoApiKey || 'SQN2Ls';

  // Localized strings via window.YJ.strings.wishlist (see layout/theme.liquid),
  // with English fallbacks.
  const BIS_STRINGS = {
    bis_title: 'Notify Me When Back in Stock',
    bis_notify_me: 'Notify Me',
    bis_email_placeholder: 'your@email.com',
    bis_success: "✓ You're on the list! We'll email you when this is back.",
    bis_error: 'Something went wrong. Please try again.',
    bis_subtitle_named: "Enter your email below and we'll let you know as soon as __TITLE__ is available again.",
    bis_subtitle_generic: "Enter your email below and we'll let you know as soon as this is available again.",
  };

  function tBis(key) {
    return window.YJ?.strings?.wishlist?.[key] ?? BIS_STRINGS[key] ?? '';
  }

  function ensureModal() {
    let modal = document.getElementById('WishlistBisModal');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'WishlistBisModal';
    modal.className = 'wishlist-bis-modal';
    modal.innerHTML = `
      <div class="wishlist-bis-modal__overlay"></div>
      <div class="wishlist-bis-modal__dialog" role="dialog" aria-modal="true" aria-label="${tBis('bis_title')}" tabindex="-1">
        <button type="button" class="wishlist-bis-modal__close" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z" fill="currentColor"/>
          </svg>
        </button>
        <div class="wishlist-bis-modal__body">
          <h2 class="wishlist-bis-modal__title">${tBis('bis_title')}</h2>
          <p class="wishlist-bis-modal__subtitle"></p>
          <form class="wishlist-bis-modal__form">
            <input type="email" class="wishlist-bis-modal__email" placeholder="${tBis('bis_email_placeholder')}" required aria-label="Email address">
            <button type="submit" class="wishlist-bis-modal__submit button button--primary">${tBis('bis_notify_me')}</button>
          </form>
          <p class="wishlist-bis-modal__success" hidden>${tBis('bis_success')}</p>
          <p class="wishlist-bis-modal__error" hidden>${tBis('bis_error')}</p>
        </div>
      </div>`;
    document.body.appendChild(modal);

    modal.querySelector('.wishlist-bis-modal__overlay').addEventListener('click', close);
    modal.querySelector('.wishlist-bis-modal__close').addEventListener('click', close);
    modal.querySelector('.wishlist-bis-modal__form').addEventListener('submit', submit);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) close();
    });
    return modal;
  }

  function open(variantId, productTitle) {
    const modal = ensureModal();
    modal.dataset.variantId = variantId || '';

    const subtitle = modal.querySelector('.wishlist-bis-modal__subtitle');
    if (subtitle) {
      subtitle.textContent = productTitle
        ? tBis('bis_subtitle_named').replace('__TITLE__', productTitle)
        : tBis('bis_subtitle_generic');
    }

    // Reset to a clean form state on every open.
    const form = modal.querySelector('.wishlist-bis-modal__form');
    form.style.display = '';
    form.reset();
    modal.querySelector('.wishlist-bis-modal__success').hidden = true;
    modal.querySelector('.wishlist-bis-modal__error').hidden = true;
    modal.querySelector('.wishlist-bis-modal__submit').disabled = false;

    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    modal.querySelector('.wishlist-bis-modal__email')?.focus();
  }

  function close() {
    const modal = document.getElementById('WishlistBisModal');
    if (!modal) return;
    modal.classList.remove('active');
    // Keep the scroll lock if the drawer behind us is still open.
    if (!document.querySelector('wishlist-drawer.active')) {
      document.body.style.overflow = '';
    }
  }

  async function submit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const modal = document.getElementById('WishlistBisModal');
    const variantId = modal?.dataset.variantId;
    const email = form.querySelector('.wishlist-bis-modal__email').value;
    const submitBtn = form.querySelector('[type=submit]');
    const successEl = modal.querySelector('.wishlist-bis-modal__success');
    const errorEl = modal.querySelector('.wishlist-bis-modal__error');

    submitBtn.disabled = true;
    successEl.hidden = true;
    errorEl.hidden = true;

    try {
      await subscribe(variantId, email);
      successEl.hidden = false;
      form.style.display = 'none';
    } catch (err) {
      console.error('Wishlist BIS submit failed:', err);
      errorEl.hidden = false;
      submitBtn.disabled = false;
    }
  }

  /**
   * Subscribes an email to a variant's back-in-stock alert via Klaviyo.
   * Shared by the modal form and by direct (modal-less) subscriptions for
   * logged-in customers whose email is already known. Throws on failure so
   * callers can surface their own error UI.
   */
  async function subscribe(variantId, email) {
    const locale = window.Shopify?.locale || document.documentElement.lang || 'en';

    const res = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=' + COMPANY_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2026-04-15' },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } },
          },
        },
      }),
    });

    if (res.status !== 202) throw new Error('BIS subscription failed: ' + res.status);

    // Fire-and-forget locale update so Klaviyo can localise the alert email.
    fetch('https://a.klaviyo.com/client/profiles?company_id=' + COMPANY_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2026-04-15' },
      body: JSON.stringify({ data: { type: 'profile', attributes: { email, locale } } }),
    });
  }

  window.WishlistBIS = { open, close, subscribe };
})();
